/**
 * QrScanner — Componente riutilizzabile per scansione QR via camera.
 * Usa html5-qrcode. Si attiva/disattiva con il prop `active`.
 */
import { useEffect, useRef } from 'react'
import { Html5Qrcode } from 'html5-qrcode'

interface Props {
  active: boolean
  onScan: (result: string) => void
  onError?: (err: string) => void
}

const QR_ELEMENT_ID = 'qr-reader-container'

export default function QrScanner({ active, onScan, onError }: Props) {
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const isRunningRef = useRef(false)
  const firedRef = useRef(false)

  useEffect(() => {
    if (!active) return

    firedRef.current = false
    const scanner = new Html5Qrcode(QR_ELEMENT_ID)
    scannerRef.current = scanner

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => {
        if (firedRef.current) return
        firedRef.current = true
        onScan(decodedText)
      },
      (err) => {
        onError?.(err)
      },
    ).then(() => {
      isRunningRef.current = true
    }).catch((err) => {
      console.error('QR scanner start error:', err)
    })

    // Cleanup: blocca callback pendenti e ferma la camera una sola volta
    return () => {
      firedRef.current = true
      if (isRunningRef.current) {
        isRunningRef.current = false
        scanner.stop().then(() => scanner.clear()).catch(() => {})
      }
    }
  }, [active])

  return (
    <div
      id={QR_ELEMENT_ID}
      style={{
        width: '100%',
        maxWidth: 400,
        margin: '0 auto',
        display: active ? 'block' : 'none',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    />
  )
}
